"use client";

import { cn } from "@jesus-central-church/shared";
import {
  type QuickFilter,
  type ViewMode,
  EventsEmptyState,
  EventsFilters,
  EventsList,
  EventsQuickBar,
  EventsTimeline,
  EventUI,
  getAvailableMonths,
  groupEventsByMonth,
} from "@jesus-central-church/ui";
import { useMemo, useState } from "react";

interface EventListClientComponentProperties {
  events: EventUI[];
  className?: string;
}

export function EventListClientComponent({
  events,
  className,
}: EventListClientComponentProperties) {
  const [searchQuery, setSearchQuery] = useState("");
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [viewMode, setViewMode] = useState<ViewMode>("timeline");

  // Get available months from all events
  const availableMonths = useMemo(() => getAvailableMonths(events), [events]);

  // Filter events based on all criteria
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          event.title.toLowerCase().includes(query) ||
          event.excerpt?.toLowerCase().includes(query) ||
          event.location?.toLowerCase().includes(query) ||
          event.speaker?.toLowerCase().includes(query) ||
          event.typeLabel?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Quick filter (time-based)
      if (quickFilter !== "all") {
        const now = new Date();
        const eventDate = new Date(event.startDateTime);

        if (quickFilter === "week") {
          const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          if (eventDate < now || eventDate > weekFromNow) return false;
        }

        if (quickFilter === "month") {
          const monthFromNow = new Date(
            now.getTime() + 30 * 24 * 60 * 60 * 1000,
          );
          if (eventDate < now || eventDate > monthFromNow) return false;
        }
      }

      // Month filter
      if (selectedMonth !== "all") {
        const eventDate = new Date(event.startDateTime);
        const eventMonthKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, "0")}`;
        if (eventMonthKey !== selectedMonth) return false;
      }

      // Type filter
      if (selectedType !== "all" && event.type !== selectedType) return false;

      return true;
    });
  }, [events, searchQuery, quickFilter, selectedMonth, selectedType]);

  // Group events by month for timeline view
  const groupedEvents = useMemo(
    () => groupEventsByMonth(filteredEvents),
    [filteredEvents],
  );

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery("");
    setQuickFilter("all");
    setSelectedMonth("all");
    setSelectedType("all");
  };

  return (
    <div className={cn("bg-boldness", className)}>
      {/* Quick Actions Bar */}
      <EventsQuickBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        quickFilter={quickFilter}
        onQuickFilterChange={setQuickFilter}
      />

      {/* Filters */}
      <EventsFilters
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
        selectedType={selectedType}
        onTypeChange={setSelectedType}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        availableMonths={availableMonths}
      />

      {/* Results */}
      <div className="container mx-auto px-6 py-12 md:px-12">
        {filteredEvents.length === 0 ? (
          <EventsEmptyState onReset={resetFilters} />
        ) : // eslint-disable-next-line unicorn/no-nested-ternary
        viewMode === "timeline" ? (
          <EventsTimeline groups={groupedEvents} />
        ) : (
          <EventsList events={filteredEvents} />
        )}
      </div>
    </div>
  );
}
